import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Palette, Droplet, Sun, Moon, Check, Copy, CheckCircle, RefreshCw } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';

interface ColorSwatch {
    name: string;
    hex: string;
    hsl: { h: number; s: number; l: number };
}

const HARMONY_TYPES = [
    { id: 'complementary', label: 'Complementary', desc: 'Opposite on color wheel' },
    { id: 'analogous', label: 'Analogous', desc: 'Adjacent colors' },
    { id: 'triadic', label: 'Triadic', desc: 'Three evenly spaced' },
    { id: 'split', label: 'Split-Complementary', desc: 'Base + 2 adjacent to complement' },
];

export const ColorTheory = () => {
    const { completeModule } = useAcademyStore();
    const [baseHue, setBaseHue] = useState(210);
    const [saturation, setSaturation] = useState(70);
    const [lightness, setLightness] = useState(50);
    const [harmony, setHarmony] = useState('complementary');
    const [copied, setCopied] = useState<string | null>(null);
    const [experiments, setExperiments] = useState(0);
    const [completed, setCompleted] = useState(false);

    const hslToHex = (h: number, s: number, l: number): string => {
        s /= 100;
        l /= 100;
        const a = s * Math.min(l, 1 - l);
        const f = (n: number) => {
            const k = (n + h / 30) % 12;
            const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
            return Math.round(255 * color).toString(16).padStart(2, '0');
        };
        return `#${f(0)}${f(8)}${f(4)}`;
    };

    const generatePalette = (): ColorSwatch[] => {
        const base: ColorSwatch = {
            name: 'Base',
            hex: hslToHex(baseHue, saturation, lightness),
            hsl: { h: baseHue, s: saturation, l: lightness },
        };

        const colors: ColorSwatch[] = [base];

        switch (harmony) {
            case 'complementary':
                colors.push({
                    name: 'Complement',
                    hex: hslToHex((baseHue + 180) % 360, saturation, lightness),
                    hsl: { h: (baseHue + 180) % 360, s: saturation, l: lightness },
                });
                break;
            case 'analogous':
                colors.push(
                    { name: 'Analog 1', hex: hslToHex((baseHue + 30) % 360, saturation, lightness), hsl: { h: (baseHue + 30) % 360, s: saturation, l: lightness } },
                    { name: 'Analog 2', hex: hslToHex((baseHue - 30 + 360) % 360, saturation, lightness), hsl: { h: (baseHue - 30 + 360) % 360, s: saturation, l: lightness } },
                );
                break;
            case 'triadic':
                colors.push(
                    { name: 'Triad 1', hex: hslToHex((baseHue + 120) % 360, saturation, lightness), hsl: { h: (baseHue + 120) % 360, s: saturation, l: lightness } },
                    { name: 'Triad 2', hex: hslToHex((baseHue + 240) % 360, saturation, lightness), hsl: { h: (baseHue + 240) % 360, s: saturation, l: lightness } },
                );
                break;
            case 'split':
                colors.push(
                    { name: 'Split 1', hex: hslToHex((baseHue + 150) % 360, saturation, lightness), hsl: { h: (baseHue + 150) % 360, s: saturation, l: lightness } },
                    { name: 'Split 2', hex: hslToHex((baseHue + 210) % 360, saturation, lightness), hsl: { h: (baseHue + 210) % 360, s: saturation, l: lightness } },
                );
                break;
        }

        // Add light/dark variants
        colors.push(
            { name: 'Light', hex: hslToHex(baseHue, saturation, Math.min(lightness + 30, 95)), hsl: { h: baseHue, s: saturation, l: Math.min(lightness + 30, 95) } },
            { name: 'Dark', hex: hslToHex(baseHue, saturation, Math.max(lightness - 30, 10)), hsl: { h: baseHue, s: saturation, l: Math.max(lightness - 30, 10) } },
        );

        return colors;
    };

    const palette = generatePalette();

    const copyColor = (hex: string) => {
        navigator.clipboard.writeText(hex);
        setCopied(hex);
        setTimeout(() => setCopied(null), 1500);
    };

    const handleChange = () => {
        setExperiments(e => e + 1);
    };

    useEffect(() => {
        if (experiments >= 5 && !completed) {
            setCompleted(true);
            completeModule('color-theory', 100);
        }
    }, [experiments, completed, completeModule]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-100 font-sans">
            <div className="bg-white shadow-sm px-8 py-4 flex justify-between items-center sticky top-0 z-30">
                <Link to="/" className="flex items-center gap-2 text-slate-500 font-bold hover:text-purple-600">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="flex items-center gap-4">
                    {completed && <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1"><CheckCircle size={14} /> Complete</div>}
                    <div className="bg-purple-100 text-purple-800 px-4 py-1 rounded-full text-sm font-bold">
                        🎨 Color Theory
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-black font-comic text-slate-800 mb-2">
                        <Palette className="inline text-purple-600 mr-3" size={40} />
                        The Color Mixer
                    </h1>
                    <p className="text-slate-600 max-w-2xl mx-auto">
                        Colors that <strong>harmonize</strong> create pleasing designs.
                        Learn about color relationships using the color wheel!
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Controls */}
                    <div className="bg-white rounded-3xl p-6 shadow-xl border-2 border-slate-100 space-y-6">
                        <div>
                            <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                                <Droplet className="text-purple-500" /> Base Color
                            </h3>

                            {/* Hue */}
                            <div className="mb-4">
                                <div className="flex justify-between mb-2">
                                    <span className="text-sm font-bold text-slate-600">Hue</span>
                                    <span className="font-mono text-purple-600">{baseHue}°</span>
                                </div>
                                <input
                                    type="range" min="0" max="360" value={baseHue}
                                    onChange={e => { setBaseHue(parseInt(e.target.value)); handleChange(); }}
                                    className="w-full h-3 rounded-full appearance-none cursor-pointer"
                                    style={{ background: 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)' }}
                                />
                            </div>

                            {/* Saturation */}
                            <div className="mb-4">
                                <div className="flex justify-between mb-2">
                                    <span className="text-sm font-bold text-slate-600">Saturation</span>
                                    <span className="font-mono text-purple-600">{saturation}%</span>
                                </div>
                                <input
                                    type="range" min="0" max="100" value={saturation}
                                    onChange={e => { setSaturation(parseInt(e.target.value)); handleChange(); }}
                                    className="w-full accent-purple-500"
                                />
                            </div>

                            {/* Lightness */}
                            <div className="mb-4">
                                <div className="flex justify-between mb-2">
                                    <span className="text-sm font-bold text-slate-600">Lightness</span>
                                    <span className="font-mono text-purple-600">{lightness}%</span>
                                </div>
                                <input
                                    type="range" min="10" max="90" value={lightness}
                                    onChange={e => { setLightness(parseInt(e.target.value)); handleChange(); }}
                                    className="w-full accent-purple-500"
                                />
                            </div>
                        </div>

                        <div>
                            <h3 className="font-bold text-slate-700 mb-3">Color Harmony</h3>
                            <div className="space-y-2">
                                {HARMONY_TYPES.map(h => (
                                    <button
                                        key={h.id}
                                        onClick={() => { setHarmony(h.id); handleChange(); }}
                                        className={`w-full p-3 rounded-xl text-left border-2 transition-all ${harmony === h.id ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-purple-200'
                                            }`}
                                    >
                                        <div className="font-bold text-sm">{h.label}</div>
                                        <div className="text-xs text-slate-500">{h.desc}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Palette Display */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Color Swatches */}
                        <div className="bg-white rounded-3xl p-6 shadow-xl border-2 border-slate-100">
                            <h3 className="font-bold text-slate-700 mb-4">Generated Palette</h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {palette.map(color => (
                                    <motion.div
                                        key={color.name}
                                        layout
                                        className="group cursor-pointer"
                                        onClick={() => copyColor(color.hex)}
                                    >
                                        <div
                                            className="h-24 rounded-xl shadow-lg relative overflow-hidden"
                                            style={{ backgroundColor: color.hex }}
                                        >
                                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/30 transition-opacity">
                                                {copied === color.hex ? (
                                                    <Check className="text-white" size={24} />
                                                ) : (
                                                    <Copy className="text-white" size={24} />
                                                )}
                                            </div>
                                        </div>
                                        <div className="mt-2">
                                            <div className="font-bold text-sm text-slate-700">{color.name}</div>
                                            <div className="font-mono text-xs text-slate-500 uppercase">{color.hex}</div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Preview */}
                        <div
                            className="rounded-3xl p-8 shadow-xl"
                            style={{ backgroundColor: palette[palette.length - 1]?.hex || '#333' }}
                        >
                            <h2
                                className="text-3xl font-black mb-4"
                                style={{ color: palette[0]?.hex }}
                            >
                                Sample UI Preview
                            </h2>
                            <p
                                className="mb-6"
                                style={{ color: palette[palette.length - 2]?.hex }}
                            >
                                See how your palette looks in a real design context!
                            </p>
                            <button
                                className="px-6 py-3 rounded-xl font-bold"
                                style={{ backgroundColor: palette[1]?.hex || palette[0]?.hex, color: '#fff' }}
                            >
                                Action Button
                            </button>
                        </div>

                        <div className="text-center text-slate-500 text-sm">
                            💡 Make 5 adjustments to complete! Progress: {Math.min(experiments, 5)}/5
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ColorTheory;
